import React, { useState, useEffect } from 'react';
import { useLanguage } from './contexts/LanguageContext';
import { getTierMeta } from './utils/tierMeta';

const BOT_TYPE_LABELS = {
  smart: 'smart',
  aggressive: 'aggro',
  conservative: 'beginner',
  veteran: 'veteran',
  master: 'master',
  grandmaster: 'grandmaster',
  jev: 'jev'
};

function Room({ socket, roomInfo, currentUserId, onLeaveRoom, onGameStart }) {
  const { t } = useLanguage();
  const [room, setRoom] = useState(roomInfo);
  const [isHost, setIsHost] = useState(false);
  const socketId = socket?.id;

  const renderTierBadge = (tier, size = 'md') => {
    const meta = getTierMeta(tier);
    const label = t(meta.labelKey) || meta.defaultLabel;
    const sizeClass = size === 'small' ? 'tier-badge--small' : '';
    return (
      <span className={`tier-badge tier-badge-${meta.level} ${sizeClass}`.trim()} title={label}>
        {meta.icon}
      </span>
    );
  };

  const getBotTypeLabel = (type) => BOT_TYPE_LABELS[type] || 'Bot';

  useEffect(() => {
    if (!currentUserId) {
      console.log('Waiting for currentUserId...');
      return;
    }

    const handleRoomUpdate = (updatedRoom) => {
      console.log('Room update received:', updatedRoom);
      setRoom(updatedRoom);
    };

    const handleGameStarted = (data) => {
      onGameStart(data.gameState);
    };

    const handleUserDisconnected = (data) => {
      console.log(`User ${data.username} disconnected`);
    };

    const handleError = (errorData) => {
      console.error('🚨 Socket error:', errorData);
      alert(`Error: ${errorData.message}`);
    };

    socket.on('roomUpdate', handleRoomUpdate);
    socket.on('gameStarted', handleGameStarted);
    socket.on('userDisconnected', handleUserDisconnected);
    socket.on('error', handleError);

    return () => {
      socket.off('roomUpdate', handleRoomUpdate);
      socket.off('gameStarted', handleGameStarted);
      socket.off('userDisconnected', handleUserDisconnected);
      socket.off('error', handleError);
    };
  }, [socket, currentUserId, onGameStart]);

  useEffect(() => {
    if (!currentUserId && !socketId) {
      setIsHost(false);
      return;
    }
    const hostMatch = room?.hostId === currentUserId || (!!socketId && room?.hostId === socketId);
    setIsHost(Boolean(hostMatch));
  }, [room?.hostId, currentUserId, socketId]);

  const handleAddBot = (slotIndex) => {
    // No botType: the server owns the default. Sending 'smart' from here meant
    // changing the configured default had no effect on the Add Bot button,
    // because the client was overriding it on every call.
    socket.emit('addBot', {
      roomId: room.roomId,
      slotIndex
    });
  };

  const handleRemoveBot = (slotIndex) => {
    socket.emit('removeBot', { 
      roomId: room.roomId, 
      slotIndex 
    });
  };

  const handleToggleReady = () => {
    socket.emit('toggleReady', { 
      roomId: room.roomId 
    });
  };

  const handleStartGame = () => {
    socket.emit('startGame', { 
      roomId: room.roomId 
    });
  };

  const handleLeaveRoom = () => {
    socket.emit('leaveRoom');
    onLeaveRoom();
  };

  const handleSitHere = (slotIndex) => {
    console.log('🪑 Attempting to sit in slot:', slotIndex);
    console.log('🪑 Room ID:', room.roomId);

    // Send event to server to change seat
    socket.emit('changeSeat', {
      roomId: room.roomId,
      targetSlot: slotIndex
    });

    console.log('🪑 ChangeSeat event emitted');
  };

  const handleCycleBotType = (slotIndex) => {
    console.log('🤖 Cycling bot type for slot:', slotIndex);
    console.log('🤖 Room ID:', room.roomId);

    // Send event to server to cycle bot type
    socket.emit('cycleBotType', {
      roomId: room.roomId,
      slotIndex: slotIndex
    });

    console.log('🤖 CycleBotType event emitted');
  };

  // Create slots array (4 slots total)
  const slots = [];
  for (let i = 0; i < room.maxPlayers; i++) {
    const player = room.players.find(p => p && p.slot === i);
    slots.push({ index: i, player });
  }

  // Get current player
  const currentPlayer = room.players.find(p => {
    if (!p) return false;
    if (currentUserId && p.id === currentUserId) return true;
    if (socketId && p.socketId === socketId) return true;
    return false;
  });
  console.log('Current player found:', currentPlayer);
  console.log('Is host?', isHost);
  console.log('CurrentUserId:', currentUserId);

  // Show loading if waiting for socket connection
  if (!currentUserId) {
    return (
      <div className="room-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="waiting-dots" style={{ marginBottom: '20px' }}>
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
          </div>
          <p style={{ color: '#666', fontSize: '18px' }}>Connecting to room...</p>
        </div>
      </div>
    );
  }

  // Convert room ID to display name (e.g., "lightning-falcon-ke9k" -> "Lightning Falcon")
  const getRoomDisplayName = (roomId) => {
    // Handle old format room-1234567890
    if (roomId.startsWith('room-')) {
      return roomId;
    }
    
    // Handle new format: "lightning-falcon-ke9k" -> "Lightning Falcon"
    // Split by dash and take only first two parts (ignore hash suffix)
    const parts = roomId.split('-');
    if (parts.length >= 2) {
      const adjective = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
      const animal = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
      return `${adjective} ${animal}`;
    }
    
    return roomId; // Fallback to original ID
  };

  return (
    <div className="room-container">
      <div className="room-header">
        <h2>{`${t('room.room') || 'Kamer'} ${getRoomDisplayName(room.roomId)}`}</h2>
        <button onClick={handleLeaveRoom} className="leave-btn">{t('room.leaveRoom') || 'Leave Room'}</button>
      </div>

      <div className="room-status" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
        <p style={{ margin: 0 }}>{t('room.players') || 'Players'}: {room.players.length}/{room.maxPlayers}</p>
        {isHost && <span className="host-badge" style={{ padding: '4px 8px', fontSize: '12px' }}>👑 {t('room.host') || 'Host'}</span>}
      </div>

      <div className="player-slots">
        <div className="slots-grid">
          {slots.map(({ index, player }) => (
            <div key={index} className={`player-slot slot-${index} ${player ? 'occupied' : 'empty'}`}>
              <div className="slot-header" style={{ alignItems: 'center' }}>
                <span className="slot-number">Slot {index + 1}</span>
                <span className="team-badge">Team {index % 2 === 0 ? 'A' : 'B'}</span>
              </div>
              
              {player ? (
                <div className="player-info">
                  <div className="player-name">
                    {player.isBot && isHost ? (
                      <button
                        className={`bot-name-btn bot-${player.botType || 'smart'}`}
                        onClick={() => handleCycleBotType(index)}
                        data-tooltip={`Click to change bot type (currently: ${getBotTypeLabel(player.botType || 'smart')})`}
                      >
                        {player.username}
                        <span className="bot-icon"> 🤖</span>
                        <span className="bot-type-pill">{getBotTypeLabel(player.botType || 'smart')}</span>
                      </button>
                    ) : (
                      <>
                        <span className="player-name-text">
                          {player.username}
                          {!player.isBot && renderTierBadge(player.challengeTier || 0, 'small')}
                        </span>
                        {player.id === room.hostId && <span className="host-icon" aria-label="Host">⭐</span>}
                        {player.isBot && <span className="bot-icon"> 🤖</span>}
                      </>
                    )}
                  </div>
                  {player.isBot && !isHost && (
                    <div className="bot-type-pill read-only">
                      {getBotTypeLabel(player.botType || 'smart')}
                    </div>
                  )}
                  {!player.isBot && (
                    <div className="player-status">
                      <span className={`ready-status ${player.isReady ? 'ready' : 'not-ready'}`}>
                        {player.isReady ? 'ready' : 'not ready'}
                      </span>
                    </div>
                  )}
                  {isHost && player.isBot && (
                    <button 
                      onClick={() => handleRemoveBot(index)}
                      className="remove-bot-btn"
                    >
                      Remove Bot
                    </button>
                  )}
                </div>
              ) : (
                <div className="empty-slot">
                  <button className="sit-here-btn" onClick={() => handleSitHere(index)}>
                    {t('room.emptySlot') || 'Empty'}
                  </button>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
                    {/* TODO: Re-enable 'Sit Here' button when backend supports seat changing */}
                    {/* Host can still add bots */}
                    {isHost && (
                      <button 
                        onClick={() => handleAddBot(index)}
                        className="add-bot-btn"
                        style={{
                          background: 'linear-gradient(135deg, #9C27B0, #7B1FA2)',
                          color: 'white',
                          padding: '10px 20px',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          transition: 'all 0.3s',
                          boxShadow: '0 2px 12px rgba(156, 39, 176, 0.4)',
                          width: '100%'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = 'translateY(-2px)';
                          e.target.style.boxShadow = '0 4px 20px rgba(156, 39, 176, 0.6)';
                          e.target.style.background = 'linear-gradient(135deg, #AB47BC, #8E24AA)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 12px rgba(156, 39, 176, 0.4)';
                          e.target.style.background = 'linear-gradient(135deg, #9C27B0, #7B1FA2)';
                        }}
                      >
                        🤖 {t('room.addBot') || 'Add Bot'}
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="room-teams">
        <div className="team team-a">
          <h4>Team A (Slots 1 & 3)</h4>
          <ul>
            {room.players.filter(p => p && p.team === 0).map(p => (
              <li key={p.id}>{p.username}</li>
            ))}
          </ul>
        </div>
        <div className="team team-b">
          <h4>Team B (Slots 2 & 4)</h4>
          <ul>
            {room.players.filter(p => p && p.team === 1).map(p => (
              <li key={p.id}>{p.username}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="room-actions">
        {currentPlayer && !currentPlayer.isBot && (
          <button 
            onClick={handleToggleReady}
            className={`ready-btn ${currentPlayer.isReady ? 'ready' : 'not-ready'}`}
          >
            {currentPlayer.isReady ? 'ready' : 'not ready'}
          </button>
        )}
        
        {isHost && (
            <button
              onClick={handleStartGame}
              disabled={!room.canStart}
              className="start-game-btn"
            >
              {room.canStart ? (t('room.startGame') || 'Start Game') : `${t('room.waitingForPlayers') || 'Waiting for players...'} (${room.players.length}/4)`}
            </button>
        )}
      </div>

    </div>
  );
}

export default Room;
